import { KiasiHistorySummaryReadReport, KiasiVolumeBar, KiasiVolumeProfile } from './contracts';
import type { KiasiAdjustmentMode, KiasiInterval, KiasiProfileMode } from './contracts';
import type { AxiosInstance } from 'axios';
import { z } from 'zod';

export { KIASI_PRICE_SCALE } from './contracts';
export type {
    KiasiAdjustmentMode,
    KiasiHistorySummaryReadReport,
    KiasiHistorySummaryRow,
    KiasiInterval,
    KiasiPriceLevel,
    KiasiProfileMode,
    KiasiVolumeBar,
    KiasiVolumeProfile,
} from './contracts';

const NANOS_PER_MILLI = BigInt(1_000_000);

/**
 * Kiasi takes `from`/`to` as Unix nanoseconds. Those integers exceed Number's
 * exact range, so the parameter is built with BigInt and sent as a decimal
 * string; axios serializes it verbatim.
 */
export function toKiasiTimestampParam(epochMs: number): string {
    return (BigInt(Math.trunc(epochMs)) * NANOS_PER_MILLI).toString();
}

export interface KiasiHistoryRange {
    readonly symbol: string;
    readonly interval: KiasiInterval;
    /** Inclusive, compared against each bar's open time. */
    readonly fromMs: number;
    /** Inclusive, compared against each bar's open time. */
    readonly toMs: number;
    /** Corporate-action adjustment; the server default is `total_return`. */
    readonly adjust?: KiasiAdjustmentMode;
}

export interface KiasiProfileQuery extends KiasiHistoryRange {
    readonly mode: KiasiProfileMode;
}

export interface KiasiBarsQuery extends KiasiHistoryRange {
    readonly limit?: number;
}

export interface KiasiRequestOptions {
    readonly signal?: AbortSignal;
}

const KiasiVolumeBars = z.array(KiasiVolumeBar);

function barsPath(symbol: string): string {
    return `/bars/${encodeURIComponent(symbol)}`;
}

function rangeParams(range: KiasiHistoryRange): Record<string, string> {
    return {
        interval: range.interval,
        from: toKiasiTimestampParam(range.fromMs),
        to: toKiasiTimestampParam(range.toMs),
        ...(range.adjust !== undefined ? { adjust: range.adjust } : {}),
    };
}

/** Aggregated volume profile over every bar in the range. */
export async function getKiasiVolumeProfile(
    api: AxiosInstance,
    query: KiasiProfileQuery,
    { signal }: KiasiRequestOptions = {},
): Promise<KiasiVolumeProfile> {
    const response = await api.get<unknown>(`${barsPath(query.symbol)}/profile`, {
        params: { ...rangeParams(query), mode: query.mode },
        signal,
    });
    return KiasiVolumeProfile.parse(response.data);
}

/** Bars with their histograms. Prefer {@link getKiasiBarsSummary} when levels are not needed. */
export async function getKiasiBars(
    api: AxiosInstance,
    query: KiasiBarsQuery,
    { signal }: KiasiRequestOptions = {},
): Promise<KiasiVolumeBar[]> {
    const response = await api.get<unknown>(barsPath(query.symbol), {
        params: { ...rangeParams(query), ...(query.limit !== undefined ? { limit: String(query.limit) } : {}) },
        signal,
    });
    return KiasiVolumeBars.parse(response.data);
}

/** Bars without histograms (price summary, totals, auction volumes). */
export async function getKiasiBarsSummary(
    api: AxiosInstance,
    query: KiasiBarsQuery,
    { signal }: KiasiRequestOptions = {},
): Promise<KiasiHistorySummaryReadReport> {
    const response = await api.get<unknown>(barsPath(query.symbol), {
        params: {
            ...rangeParams(query),
            projection: 'summary',
            ...(query.limit !== undefined ? { limit: String(query.limit) } : {}),
        },
        signal,
    });
    return KiasiHistorySummaryReadReport.parse(response.data);
}
